import { clamp, type SpotlightLink } from '@fuuze/shared';
import { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

type Props = {
  max?: number;
  step?: number;
  value?: number;
  onSliderChange?: (v: number, spotlight?: SpotlightLink) => void;
  onSliderChangeEnd?: (v: number, spotlight?: SpotlightLink) => void;
  spotlights?: SpotlightLink[];
  loading?: boolean;
  disabled?: boolean;
};

const THUMB_WIDTH = 15;

export function CustomPlayerSlider({
  max = 100,
  step = 1,
  value = 0,
  onSliderChange,
  onSliderChangeEnd,
  spotlights = [],
  loading,
  disabled,
}: Props) {
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [val, setVal] = useState(value);
  const trackRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLButtonElement>(null);

  const getSegment = (v: number) => spotlights.find(e => v >= e.start && v <= e.end);

  const updateValue = (newVal: number, end = false) => {
    const clamped = clamp(newVal, 0, max);
    setVal(clamped);
    const segment = getSegment(clamped);
    if (end) onSliderChangeEnd?.(clamped, segment);
    else onSliderChange?.(clamped, segment);
  };

  const percent = (val / max) * 100;

  // No idea why I wrote this code, It seems useless
  // useEffect(() => {
  //   if (spotlights.length > 0) {
  //     updateValue(value, true);
  //   }
  // }, [spotlights]);

  useEffect(() => {
    if (isMouseDown) return;
    updateValue(value);
  }, [value, isMouseDown]);

  return (
    <Container
      $loading={loading}
      onClick={e => {
        if (disabled) return;
        const rect = trackRef.current!.getBoundingClientRect();
        const newVal = ((e.clientX - rect.left) / rect.width) * max;
        updateValue(newVal, true);
      }}
      onTouchStart={e => {
        if (disabled) return;
        e.preventDefault();
        const rect = trackRef.current!.getBoundingClientRect();
        const touch = e.touches[0];
        const newVal = ((touch!.clientX - rect.left) / rect.width) * max;
        updateValue(newVal, true);
      }}
    >
      <Track ref={trackRef}>
        <TrackLeft $disabled={disabled} $loading={loading} style={{ width: `${percent}%` }} />
        <TrackRight
          $disabled={disabled}
          $loading={loading}
          style={{ left: `${percent}%`, width: `${100 - percent}%` }}
        />
        {spotlights.map((e, i) => (
          <SpotlightBlock
            key={i}
            $loading={loading}
            style={{
              left: `${(e.start / max) * 100}%`,
              width: `${((e.end - e.start) / max) * 100}%`,
            }}
          />
        ))}
        <Thumb
          ref={thumbRef}
          tabIndex={0}
          style={{ left: `calc(${percent}% - ${THUMB_WIDTH / 2}px)`, display: loading ? 'none' : 'block' }}
          disabled={disabled}
          onKeyDown={e => {
            if (e.key === 'ArrowLeft') {
              e.preventDefault();
              const newVal = val - step;
              updateValue(newVal);
              updateValue(newVal, true);
            } else if (e.key === 'ArrowRight') {
              e.preventDefault();
              const newVal = val + step;
              updateValue(newVal);
              updateValue(newVal, true);
            }
          }}
          onMouseDown={e => {
            e.stopPropagation();
            e.preventDefault();
            setIsMouseDown(true);
            thumbRef.current?.focus();
            const startX = e.clientX;
            const startVal = val;
            const onMove = (ev: MouseEvent) => {
              const rect = trackRef.current!.getBoundingClientRect();
              const dx = ev.clientX - startX;
              const newVal = startVal + (dx / rect.width) * max;
              updateValue(newVal);
            };
            const onUp = (ev: MouseEvent) => {
              setIsMouseDown(false);
              const rect = trackRef.current!.getBoundingClientRect();
              const dx = ev.clientX - startX;
              const newVal = startVal + (dx / rect.width) * max;
              updateValue(newVal, true);
              document.removeEventListener('mousemove', onMove);
              document.removeEventListener('mouseup', onUp);
            };
            document.addEventListener('mousemove', onMove);
            document.addEventListener('mouseup', onUp);
          }}
          onTouchStart={e => {
            e.stopPropagation();
            e.preventDefault();
            setIsMouseDown(true);
            thumbRef.current?.focus();
            const touch = e.touches[0];
            const startX = touch!.clientX;
            const startVal = val;
            const onMove = (ev: TouchEvent) => {
              const rect = trackRef.current!.getBoundingClientRect();
              const touchMove = ev.touches[0];
              const dx = touchMove!.clientX - startX;
              const newVal = startVal + (dx / rect.width) * max;
              updateValue(newVal);
            };
            const onEnd = (ev: TouchEvent) => {
              setIsMouseDown(false);
              if (ev.touches.length === 0) {
                const rect = trackRef.current!.getBoundingClientRect();
                const touchEnd = ev.changedTouches[0];
                const dx = touchEnd!.clientX - startX;
                const newVal = startVal + (dx / rect.width) * max;
                updateValue(newVal, true);
                document.removeEventListener('touchmove', onMove);
                document.removeEventListener('touchend', onEnd);
              }
            };
            document.addEventListener('touchmove', onMove);
            document.addEventListener('touchend', onEnd);
          }}
        />
      </Track>
    </Container>
  );
}

const Container = styled.div<{ $loading?: boolean }>`
  position: relative;
  width: 100%;
  height: 25px;
  display: flex;
  align-items: center;
  cursor: pointer;

  ${props =>
    props.$loading &&
    css`
      pointer-events: none;
  `}
`;

const Track = styled.div`
  position: relative;
  width: 100%;
  height: 5px;
`;

const TrackLeft = styled.div<{ $loading?: boolean; $disabled?: boolean }>`
  position: absolute;
  height: 100%;
  background: ${props => (props.$disabled ? props.theme.colors.dark.dark300 : props.theme.colors.white)};
  border-radius: 3px 0 0 3px; // To not create a "hole" between tracks when the thumb is hidden

  ${props =>
    props.$loading &&
    css`
    animation: pulse 1.5s infinite;
    @keyframes pulse {
      0%,
      100% {
        background-color: ${props => props.theme.colors.dark.dark900};
      }
      50% {
        background-color: ${props => props.theme.colors.dark.dark200};
      }
    }
  `}
`;

const TrackRight = styled.div<{ $loading?: boolean; $disabled?: boolean }>`
  position: absolute;
  height: 100%;
  background: ${props => (props.$disabled ? props.theme.colors.dark.dark300 : props.theme.colors.dark.dark200)};
  border-radius: 0 3px 3px 0;

  ${props =>
    props.$loading &&
    css`
    animation: pulse 1.5s infinite;
    @keyframes pulse {
      0%,
      100% {
        background-color: ${props => props.theme.colors.dark.dark900};
      }
      50% {
        background-color: ${props => props.theme.colors.dark.dark200};
      }
    }
  `}
`;

const SpotlightBlock = styled.div<{ $loading?: boolean }>`
  position: absolute;
  height: 100%;
  border-radius: 3px;
  background-color: #d76700;

  ${props =>
    props.$loading &&
    css`
    display: none;
  `}
`;

const Thumb = styled.button`
  border: 0;
  position: absolute;
  top: -5px;
  width: ${THUMB_WIDTH}px;
  height: ${THUMB_WIDTH}px;
  background: white;
  border-radius: 50%;
  cursor: pointer;
  outline-width: 2px;

  transition: transform 200ms cubic-bezier(0.25, 1, 0.5, 1);
  &:hover {
    transform: scale(1.2);
  }
`;
